import React from "react";

import {
    obterDadosPessoaisPF,
    obterDadosPessoaisPJ,
    obterTipoCliente
} from "../../../services/cliente";

import {
    guardarDadosUsuario2,
    obterDadosUsuario2
} from "../../../services/cookies";

import { renovacaoToken } from "../../../services/usuario";

import {
    ImagensDocumentacao,
    Socios
} from "../../../pages/Cliente/Cadastro/Cadastro";


export interface ClienteModal {
    tipoCliente: "PF" | "PJ";

    // Comuns
    nome?: string;
    telefone?: string;

    // PF
    cpf?: string;
    rg?: string;
    orgaoExpeditor?: string;
    ufOrgaoExpeditor?: string;
    nacionalidade?: string;
    dataNascimento?: string;
    renda?: number;
    profissao?: string;
    estadoCivil?: string;

    // PJ
    cnpj?: string;
    razaoSocial?: string;
    socios?: Socios[];

    // Endereço
    rua?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;

    // Documentos
    imagensDocumentacao?: ImagensDocumentacao[];
}


type SetCliente = React.Dispatch<
    React.SetStateAction<ClienteModal | undefined>
>;

type SetBoolean = React.Dispatch<
    React.SetStateAction<boolean>
>;


/**
 * Normaliza o retorno da Pessoa Física para o formato utilizado pelo modal.
 */
const normalizarPessoaFisica = (response: any): ClienteModal => {
    const resultado = response?.data?.resultado ?? {};
    const dados = resultado?.dadosPessoais ?? resultado;

    const endereco =
        resultado?.endereco ??
        dados?.endereco ??
        {};

    return {
        tipoCliente: "PF",

        nome: dados?.nome,
        cpf: dados?.cpf,
        rg: dados?.rg,
        orgaoExpeditor: dados?.orgaoExpeditor,

        ufOrgaoExpeditor:
            dados?.ufOrgaoExpeditor ??
            dados?.ufOrgaoExpeditorRg,

        nacionalidade: dados?.nacionalidade,
        dataNascimento: dados?.dataNascimento,
        renda: dados?.renda,
        profissao: dados?.profissao,
        estadoCivil: dados?.estadoCivil,
        telefone: dados?.telefone,

        rua:
            endereco?.rua ??
            dados?.rua,

        complemento:
            endereco?.complemento ??
            dados?.complemento,

        bairro:
            endereco?.bairro ??
            dados?.bairro,

        cidade:
            endereco?.cidade ??
            dados?.cidade,

        estado:
            endereco?.estado ??
            dados?.estado,

        cep:
            endereco?.cep ??
            dados?.cep,

        imagensDocumentacao:
            dados?.imagensDocumentacao ?? []
    };
};


/**
 * Normaliza o retorno da Pessoa Jurídica.
 *
 * Estrutura esperada:
 *
 * resultado.dadosPessoais -> dados básicos
 * resultado.endereco      -> endereço
 * resultado.socios        -> sócios
 */
const normalizarPessoaJuridica = (response: any): ClienteModal => {
    const resultado = response?.data?.resultado;

    if (!resultado) {
        throw new Error(
            "A API não retornou os dados da pessoa jurídica."
        );
    }

    const dados = resultado?.dadosPessoais ?? {};
    const endereco = dados?.endereco ?? {};

    return {
        tipoCliente: "PJ",

        nome: dados?.nome,
        razaoSocial: dados?.razaoSocial,
        cnpj: dados?.cnpj,
        telefone: dados?.telefone,

        rua: endereco?.rua,
        complemento: endereco?.complemento,
        bairro: endereco?.bairro,
        cidade: endereco?.cidade,
        estado: endereco?.estado,
        cep: endereco?.cep,

        socios:
            Array.isArray(dados?.socios)
                ? dados.socios
                : [],

        imagensDocumentacao:
            dados?.imagensDocumentacao ?? []
    };
};

/**
 * Descobre o tipo e chama o endpoint correspondente.
 */
const obterClientePorTipo = async (
    token: string,
    idCredenciais: string
): Promise<ClienteModal> => {

    const responseTipo = await obterTipoCliente(
        token,
        idCredenciais
    );

    const tipo = responseTipo?.data?.mensagem;

    if (typeof tipo !== "string") {
        throw new Error(
            "Tipo de cliente não retornado pela API."
        );
    }

    if (/jurid/i.test(tipo)) {
        const response = await obterDadosPessoaisPJ(
            token,
            idCredenciais
        );

        const cliente = normalizarPessoaJuridica(response);

        // Pode remover depois de confirmar.
        console.log("PJ NORMALIZADA:", cliente);

        return cliente;
    }

    if (/fisic/i.test(tipo)) {
        const response = await obterDadosPessoaisPF(
            token,
            idCredenciais
        );

        return normalizarPessoaFisica(response);
    }

    throw new Error(
        `Tipo de cliente desconhecido: ${tipo}`
    );
};


/**
 * Função utilizada pelo modal.
 */
export const obterDadosCadastrais = async (
    idCredenciais: string | undefined,
    setDadosCliente: SetCliente,
    setLoading: SetBoolean,
    setErro: SetBoolean,
    setDesconectar: SetBoolean
) => {

    if (!idCredenciais) {
        setErro(true);
        return;
    }

    setLoading(true);
    setErro(false);

    const {
        email,
        urlAvatar,
        id,
        token,
        tokenRenovacao
    } = obterDadosUsuario2();

    try {
        const cliente = await obterClientePorTipo(
            token,
            idCredenciais
        );

        /*
         * IMPORTANTE:
         *
         * Não fazer:
         *
         * setDadosCliente(
         *     response.data.resultado.dadosPessoais
         * )
         *
         * O objeto já está normalizado.
         */
        setDadosCliente(cliente);

    } catch (err: any) {

        const tokenExpirado =
            err?.response?.status === 401 ||
            err
                ?.toString?.()
                .includes("status code 401");

        if (!tokenExpirado) {
            console.error(
                "Erro ao obter dados cadastrais:",
                err
            );

            setErro(true);
            return;
        }

        try {
            const renovacao = await renovacaoToken(
                token,
                tokenRenovacao
            );

            const novoToken =
                renovacao
                    .data
                    .camposAdicionais
                    .tokenAcesso;

            const novoTokenRenovacao =
                renovacao
                    .data
                    .camposAdicionais
                    .tokenRenovacao;

            guardarDadosUsuario2(
                novoToken,
                novoTokenRenovacao,
                email?.toString?.(),
                id?.toString?.(),
                urlAvatar
            );

            const cliente = await obterClientePorTipo(
                novoToken,
                idCredenciais
            );

            setDadosCliente(cliente);

        } catch (refreshError) {

            console.error(
                "Falha ao renovar token:",
                refreshError
            );

            setDesconectar(true);
        }

    } finally {
        setLoading(false);
    }
};